import { ToolCallStrategy, ToolCall, BaseParamsSchema } from './BaseToolStrategy';
import { z } from 'zod';
import crypto from 'crypto';
import {
  AgentCompletionToolSchemas,
  KratosAfmAgentSanPri225ToolSchemas,
  KratosAfmAgentPeg003ToolSchemas,
  KratosAfmAgentSanPri26111ToolSchemas,
  KratosAfmAgentSanPro282ToolSchemas,
} from './HardcodedToolCallSchemas';
import { Message } from '@scale/shared/types/chat-completion';

interface BeeOrchestratorToolCallRaw {
  id: string;
  type: string;
  function: {
    name: string;
    arguments: string;
  };
}

interface CalendarsDatabase {}
interface CalendarEventsDatabase {}
interface CalendarEventsRecurrenceStagingDatabase {}
interface SettingDatabase {}

interface InitialDatabases {
  CALENDARS: CalendarsDatabase[];
  CALENDAR_EVENTS: CalendarEventsDatabase[];
  CALENDAR_EVENTS_RECURRENCE_STAGING: CalendarEventsRecurrenceStagingDatabase[];
  SETTING: SettingDatabase[];
}

interface ConversationMetadata {
  flow: {
    description: string;
    initial_databases: InitialDatabases;
    tools: Record<string, any>[];
  };
}

interface ConversationMetadataInput {
  // This is the structure of the conversation metadata that is fed back to the model.
  // The following fields are dropped:
  // - CALENDAR_EVENTS_RECURRENCE_STAGING

  flow: {
    description: string;
    tools: Record<string, any>[];
    initial_databases: {
      CALENDARS: CalendarsDatabase[];
      CALENDAR_EVENTS: CalendarEventsDatabase[];
      SETTING: SettingDatabase[];
    };
  };
}

function conversationMetadataToInput(
  conversationMetadata: ConversationMetadata,
): ConversationMetadataInput {
  return {
    flow: {
      description: conversationMetadata.flow.description,
      tools: conversationMetadata.flow.tools,
      initial_databases: {
        CALENDARS: conversationMetadata.flow.initial_databases.CALENDARS,
        CALENDAR_EVENTS: conversationMetadata.flow.initial_databases.CALENDAR_EVENTS,
        SETTING: conversationMetadata.flow.initial_databases.SETTING,
      },
    },
  };
}

const ORCHESTRATOR_TO_TOOL_SCHEMAS: Record<string, Record<string, z.ZodTypeAny>> = {
  'afm-agent-san-pri-225': KratosAfmAgentSanPri225ToolSchemas,
  'afm-agent-peg-003': KratosAfmAgentPeg003ToolSchemas,
  'afm-agent-001': AgentCompletionToolSchemas,
  'afm-agent-san-pri-2611-1': KratosAfmAgentSanPri26111ToolSchemas,
  'afm-agent-san-pro-282': KratosAfmAgentSanPro282ToolSchemas,
  'agentsandbox-v2-8-11-prototype': KratosAfmAgentSanPro282ToolSchemas,
  'afm-agent-san-pro-2816': KratosAfmAgentSanPro282ToolSchemas,
};

export const BeeOrchestratorParamsSchema = BaseParamsSchema.extend({
  orchestrator: z.string(),
  settingDatabaseJSON: z.string().optional().describe('The setting database in JSON format.'),
  calendarDatabaseJSON: z.string().optional().describe('The calendar database in JSON format.'),
  calendarEventDatabaseJSON: z
    .string()
    .optional()
    .describe('The calendar event database in JSON format.'),
  tool_allow_list: z
    .string()
    .optional()
    .describe('The comma separated list of tool names that are allowed to be called.'),
});

type BeeOrchestratorParams = z.infer<typeof BeeOrchestratorParamsSchema>;

export class BeeOrchestratorToolCallStrategy extends ToolCallStrategy {
  params: BeeOrchestratorParams;

  constructor(params: BeeOrchestratorParams) {
    super(params);
    this.params = params;
  }

  overlayDatabases(databases: InitialDatabases): void {
    // Modify the databases in place.
    if (this.params.settingDatabaseJSON && this.params.settingDatabaseJSON.length > 0) {
      databases.SETTING = JSON.parse(this.params.settingDatabaseJSON);
    }
    if (this.params.calendarDatabaseJSON && this.params.calendarDatabaseJSON.length > 0) {
      databases.CALENDARS = JSON.parse(this.params.calendarDatabaseJSON);
    }
    if (this.params.calendarEventDatabaseJSON && this.params.calendarEventDatabaseJSON.length > 0) {
      databases.CALENDAR_EVENTS = JSON.parse(this.params.calendarEventDatabaseJSON);
    }
  }

  parseToolCalls(message: Message): ToolCall[] {
    try {
      const rawToolCalls = message.messageMetadata?.tool_calls as BeeOrchestratorToolCallRaw[];
      return rawToolCalls.map(rawToolCall => ({
        functionName: rawToolCall.function.name,
        functionParameters: JSON.parse(rawToolCall.function.arguments),
        functionMetadata: {
          id: rawToolCall.id,
          type: rawToolCall.type,
        },
      }));
    } catch (e) {
      // return an empty array if tool calls cannot be parsed
      return [];
    }
  }

  writeToolCalls(
    message: Message,
    toolCalls: ToolCall[],
  ): {
    message: Message;
    output: string;
  } {
    // Kratos tool call ids are call_<hash>, we want to retain the same hash for each tool call.
    const originalToolCalls = message.messageMetadata?.tool_calls as BeeOrchestratorToolCallRaw[];
    const originalIds = originalToolCalls?.map(toolCall => toolCall.id.split('_').pop()) ?? [];
    toolCalls.forEach((toolCall, idx) => {
      let hash = originalIds[idx];
      if (!hash) {
        // Create a random 24 character hash
        hash = crypto.randomBytes(24).toString('hex').slice(0, 24);
      }
      toolCall.functionMetadata.id = `call_${hash}`;
    });
    const newToolCalls = toolCalls.map(toolCall => ({
      id: toolCall.functionMetadata.id,
      type: toolCall.functionMetadata.type,
      function: {
        name: toolCall.functionName,
        arguments: JSON.stringify(toolCall.functionParameters),
      },
    }));

    // Overlay initial databases into the message metadata.
    const convMetadata = message.messageMetadata?.conversationMetadata as ConversationMetadata;
    if (convMetadata) {
      const initial_dbs = convMetadata?.flow?.initial_databases;
      if (initial_dbs) {
        this.overlayDatabases(initial_dbs);
      }
    }

    const newMessage = {
      ...message,
      messageMetadata: {
        ...message.messageMetadata,
        tool_calls: newToolCalls,
        conversationMetadata: convMetadata ? conversationMetadataToInput(convMetadata) : undefined,
        // Hack: the output is used for display purposes. But our system threads the output as message.content
        // so we need a way to override the content to be an empty string.
        content: '',
      },
    };

    return {
      message: newMessage,
      output: `\`\`\`json\n${JSON.stringify(
        newMessage.messageMetadata?.tool_calls,
        null,
        2,
      )}\n\`\`\``,
    };
  }

  getToolSchemas() {
    if (!this.params?.orchestrator) {
      throw new Error('Orchestrator is required');
    }
    if (this.params.tool_allow_list) {
      const allowList = this.params.tool_allow_list.split(',').map(tool => tool.trim());
      return Object.fromEntries(
        Object.entries(ORCHESTRATOR_TO_TOOL_SCHEMAS[this.params.orchestrator]).filter(([key]) =>
          allowList.includes(key),
        ),
      );
    }
    return ORCHESTRATOR_TO_TOOL_SCHEMAS[this.params.orchestrator];
  }

  createBlankToolCall() {
    return {
      functionName: '',
      functionParameters: {},
      functionMetadata: {
        type: 'TOOL_TYPE_FUNCTION',
      },
    };
  }
}
